import { use, useEffect, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import { useGenerateSecrets } from 'features/gateway/domain/queries/gateway-queries';

export interface IGatewaySettingsIoTEdgeClientHook {
  deviceId: string | undefined;
  ioTEdgeConnectionString: string;
  generateIoTEdgeConnectionStringIsLoading: boolean;
  isGenerateConfirmationOpen: boolean;
  defaultIoTEdgeConnectionString: string;

  generateIoTEdgeConnectionString: () => void;
  submitGenerateIoTEdgeConnectionString: () => void;
  closeGenerateConfirmationPopup: () => void;
}

const useGatewaySettingsIoTEdgeClient = (): IGatewaySettingsIoTEdgeClientHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const { gatewayDetailsHook } = useGatewaysContextProvider();

  const defaultIoTEdgeConnectionString = '********';

  const [isGenerateConfirmationOpen, setIsGenerateConfirmationOpen] = useState(false);
  const [deviceId, setDeviceId] = useState(gatewayDetailsHook.currentSelectedGateway?.deviceId);
  const [ioTEdgeConnectionString, setIoTEdgeConnectionString] = useState(defaultIoTEdgeConnectionString);

  const {
    mutate: generateSecrets,
    isPending: generateSecretIsPending,
    isError: generateSecretIsError,
    error: generateSecretError,
    isSuccess: generateSecretIsSuccess,
    data: generateSecretData,
  } = useGenerateSecrets();

  useEffect(() => {
    if (generateSecretIsError && generateSecretError) {
      setErrorMessage(generateSecretError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateSecretIsError]);

  useEffect(() => {
    if (generateSecretData) {
      setDeviceId(generateSecretData.deviceId);
      setIoTEdgeConnectionString(generateSecretData.ioTEdgeConnectionString ?? defaultIoTEdgeConnectionString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateSecretIsSuccess]);

  useEffect(() => {
    setDeviceId(gatewayDetailsHook.currentSelectedGateway?.deviceId);
  }, [gatewayDetailsHook.currentSelectedGateway]);

  const generateIoTEdgeConnectionString = () => {
    if (deviceId && deviceId.length > 0) {
      setIsGenerateConfirmationOpen(true);
    } else {
      const gatewayId = gatewayDetailsHook.currentSelectedGateway?.id;
      if (gatewayId) {
        generateSecrets(gatewayId);
      }
      setIsGenerateConfirmationOpen(false);
    }
  };

  const closeGenerateConfirmationPopup = () => setIsGenerateConfirmationOpen(false);

  const submitGenerateIoTEdgeConnectionString = () => {
    const gatewayId = gatewayDetailsHook.currentSelectedGateway?.id;
    if (gatewayId) {
      generateSecrets(gatewayId);
    }
    setIsGenerateConfirmationOpen(false);
  };

  return {
    deviceId,
    ioTEdgeConnectionString,
    generateIoTEdgeConnectionStringIsLoading: generateSecretIsPending,
    isGenerateConfirmationOpen,
    defaultIoTEdgeConnectionString,

    generateIoTEdgeConnectionString,
    submitGenerateIoTEdgeConnectionString,
    closeGenerateConfirmationPopup,
  };
};

export default useGatewaySettingsIoTEdgeClient;
